import { useEffect } from "react";

import { useGetClinicRequest, useGetPreClinicSteps } from "@/api/generated";
import { ClinicRequestStatus, ProgramGroup } from "@/api/generated/models";
import { ChatNote, FormattedMarkdown } from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import ClinicRequest, {
  type ClinicRequestProps,
} from "@/conversation/AssistantMessage/ClinicRequest";
import { useAnalytics } from "@/hooks/analytics";
import { useCurrentConversationId } from "@/hooks/conversation";
import {
  isKnownPreClinicStep,
  isPreClinicFlowResolved,
  useHasPreClinicStepsStoreHydrated,
  usePreClinicStepsStore,
} from "@/store";

export default function PreClinicGate(props: ClinicRequestProps) {
  const { clinicRequestId, conversationId: eventConversationId } = props;
  const urlConversationId = useCurrentConversationId();
  // The event's own conversation, not the url's. A group stream renders the clinic requests of
  // its older conversations too, and a request only resolves under the conversation that created
  // it — reading the url's would 404 for every request but the newest conversation's.
  const conversationId = eventConversationId ?? urlConversationId;
  const { track } = useAnalytics();

  const { value: isPaywallEnabled, loading: isPaywallFlagLoading } =
    useFeatureFlag("chai-paywall-user-progression", false);

  const { data: clinicRequest, isLoading: isClinicRequestLoading } =
    useGetClinicRequest(conversationId as string, clinicRequestId as string, {
      query: { enabled: Boolean(conversationId && clinicRequestId) },
    });
  const isAccessRequest = clinicRequest?.program?.group === ProgramGroup.access;
  const { data: preClinicSteps, isLoading: isPreClinicStepsLoading } =
    useGetPreClinicSteps(
      { clinic_request_id: clinicRequestId as string },
      {
        query: {
          enabled:
            isPaywallEnabled && !isAccessRequest && Boolean(clinicRequestId),
        },
      },
    );
  const hasPreClinicStepsHydrated = useHasPreClinicStepsStoreHydrated();
  const preClinicProgress = usePreClinicStepsStore((s) =>
    clinicRequestId ? s.progressByClinicRequestId[clinicRequestId] : undefined,
  );
  const startPreClinic = usePreClinicStepsStore((s) => s.startPreClinic);
  const isPreClinicActive = usePreClinicStepsStore(
    (s) =>
      s.active?.conversationId === conversationId &&
      s.active?.clinicRequestId === clinicRequestId,
  );
  const requiredSteps = (preClinicSteps?.steps ?? []).filter(
    isKnownPreClinicStep,
  );
  const isPreClinicFinished = isPreClinicFlowResolved(
    requiredSteps,
    preClinicProgress,
  );

  const isPaywallReady =
    isPaywallEnabled &&
    !isAccessRequest &&
    clinicRequest?.status === ClinicRequestStatus.created &&
    hasPreClinicStepsHydrated &&
    !isPreClinicStepsLoading &&
    Boolean(clinicRequestId);

  const shouldCollect =
    isPaywallReady &&
    requiredSteps.length > 0 &&
    !isPreClinicFinished &&
    Boolean(conversationId);

  const shouldTrackPaywallPresented =
    isPaywallReady && isPreClinicFinished && !isPreClinicActive;

  useEffect(() => {
    if (!shouldTrackPaywallPresented) return;
    const encounterType = clinicRequest?.type;
    const visitType = clinicRequest?.visit_type;
    track(AD_CONVERSION_EVENTS.paywallPresented, {
      conversation_id: clinicRequest?.origin_conversation_id ?? conversationId,
      clinic_request_id: clinicRequestId,
      encounter_type: encounterType,
      ...(visitType && { visit_type: visitType }),
      $distinct_id: `${AD_CONVERSION_EVENTS.paywallPresented}:${clinicRequestId}`,
    });
  }, [
    clinicRequest?.origin_conversation_id,
    clinicRequest?.type,
    clinicRequest?.visit_type,
    clinicRequestId,
    conversationId,
    shouldTrackPaywallPresented,
    track,
  ]);

  useEffect(() => {
    if (shouldCollect) {
      startPreClinic(conversationId!, clinicRequestId!, requiredSteps);
    }
  }, [
    shouldCollect,
    conversationId,
    clinicRequestId,
    requiredSteps,
    startPreClinic,
  ]);

  if (isClinicRequestLoading || (isPaywallFlagLoading && !isAccessRequest)) {
    // Don't show the skeleton so it doesn't flash for a moment before the panel appears
    return null;
  }
  if (
    clinicRequest?.status === ClinicRequestStatus.created &&
    (shouldCollect || isPreClinicActive)
  ) {
    return (
      <FormattedMarkdown markdown="Before we continue, please answer a few questions so we can prepare your care request." />
    );
  }
  if (
    isAccessRequest ||
    !isPaywallEnabled ||
    clinicRequest?.status !== ClinicRequestStatus.created
  ) {
    return <ClinicRequest {...props} />;
  }
  if (!hasPreClinicStepsHydrated || isPreClinicStepsLoading) {
    return <ChatNote.Skeleton />;
  }
  return <ClinicRequest {...props} />;
}
